import React, { useRef, useState } from 'react';
import { Calendar, MapPin, Phone, Mail, Sparkles, Download, Loader2 } from 'lucide-react';
import { toPng } from 'html-to-image';
import { Button } from '../ui/button';
import SocialShareButtons from './SocialShareButtons';
import JamboreeQR from './JamboreeQR';
import { useCheerEvent } from '../../hooks/useCheerEvent';

// Dynamic, code-driven flyer (mirrors the PricingFlyer approach so the
// document stays in sync with mock.js content without re-uploading images).

const WRAPPER_STYLE = {
  maxWidth: '720px',
  margin: '0 auto',
  borderRadius: '20px',
  overflow: 'hidden',
  position: 'relative',
  background:
    'radial-gradient(circle at 20% 0%, rgba(255, 215, 0, 0.18) 0%, transparent 45%), ' +
    'radial-gradient(circle at 100% 100%, rgba(255, 215, 0, 0.12) 0%, transparent 40%), ' +
    'linear-gradient(155deg, #050505 0%, #0d0a00 55%, #050505 100%)',
  border: '2px solid rgba(255, 215, 0, 0.55)',
  boxShadow: '0 25px 80px rgba(255, 215, 0, 0.22)',
  padding: 'clamp(1.5rem, 4vw, 2.75rem) clamp(1.25rem, 3vw, 2.25rem)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'clamp(1rem, 2.5vw, 1.75rem)',
  color: '#fff',
};

const HEADER_STYLE = {
  textAlign: 'center',
  marginBottom: 'clamp(0.75rem, 2vw, 1.25rem)',
};

const KICKER_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.4rem',
  color: '#FFD700',
  fontSize: '0.75rem',
  letterSpacing: '3px',
  fontWeight: 800,
  textTransform: 'uppercase',
  marginBottom: '0.4rem',
};

const TITLE_STYLE = {
  fontSize: 'clamp(1.8rem, 5vw, 2.85rem)',
  fontWeight: 900,
  letterSpacing: '1px',
  color: '#FFD700',
  lineHeight: 1,
  margin: 0,
  textShadow: '0 2px 24px rgba(255, 215, 0, 0.35)',
};

const SUBTITLE_STYLE = {
  fontSize: 'clamp(0.95rem, 2.2vw, 1.15rem)',
  letterSpacing: '4px',
  fontWeight: 700,
  color: '#fff',
  margin: '0.4rem 0 0',
  textTransform: 'uppercase',
};

const TAGLINE_STYLE = {
  color: '#FFD700',
  fontStyle: 'italic',
  marginTop: '0.5rem',
  fontSize: 'clamp(0.85rem, 2vw, 1rem)',
};

const META_ROW_STYLE = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: '0.4rem 1.25rem',
  fontSize: 'clamp(0.78rem, 1.8vw, 0.92rem)',
  margin: '0.75rem 0 0',
  color: '#f0f0f0',
};

const PROGRAM_GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(115px, 1fr))',
  gap: 'clamp(0.4rem, 1.2vw, 0.7rem)',
  margin: 'clamp(0.75rem, 2vw, 1rem) 0',
  alignItems: 'start',
};

const PROGRAM_CARD_STYLE = {
  position: 'relative',
  background: 'rgba(255, 215, 0, 0.06)',
  border: '1px solid rgba(255, 215, 0, 0.35)',
  borderRadius: '10px',
  padding: 'clamp(0.55rem, 1.5vw, 0.85rem)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  alignSelf: 'start',
};

const STEP_NUM_STYLE = {
  fontSize: '0.65rem',
  fontWeight: 800,
  letterSpacing: '2px',
  color: '#000',
  background: '#FFD700',
  borderRadius: '999px',
  padding: '2px 8px',
  marginBottom: '0.35rem',
};

const PROGRAM_TITLE_STYLE = {
  fontSize: 'clamp(0.7rem, 1.5vw, 0.82rem)',
  color: '#FFD700',
  fontWeight: 800,
  lineHeight: 1.15,
  margin: 0,
};

const CONTACT_BOX_STYLE = {
  border: '1px solid rgba(255, 215, 0, 0.45)',
  borderRadius: '12px',
  padding: 'clamp(0.65rem, 1.6vw, 0.9rem) clamp(0.85rem, 2vw, 1.1rem)',
  background: 'rgba(0, 0, 0, 0.35)',
};

const CONTACT_HEADER_STYLE = {
  textAlign: 'center',
  fontSize: '0.72rem',
  letterSpacing: '3px',
  fontWeight: 800,
  color: '#FFD700',
  marginBottom: '0.55rem',
  textTransform: 'uppercase',
};

const CONTACT_LIST_STYLE = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-around',
  gap: '0.5rem 1rem',
  fontSize: 'clamp(0.78rem, 1.8vw, 0.92rem)',
};

const EMAIL_STYLE = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.4rem',
  marginTop: '0.5rem',
  fontSize: 'clamp(0.7rem, 1.5vw, 0.82rem)',
  color: '#FFD700',
};

const CheerJamboreeFlyer = () => {
  const { event: cheerJamboree } = useCheerEvent();
  const flyerRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!flyerRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(flyerRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#050505',
      });
      const link = document.createElement('a');
      link.download = 'Randallstown-Cheer-Jamboree-2026.png';
      link.href = dataUrl;
      link.click();
    } catch {
      /* html-to-image can fail on cross-origin webfonts — silent */
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <div ref={flyerRef} data-testid="cheer-jamboree-flyer" style={WRAPPER_STYLE}>
        {/* HEADER */}
        <div style={HEADER_STYLE}>
          <div style={KICKER_STYLE}>
            <Sparkles size={14} /> Randallstown Panthers Present
          </div>
          <h2 style={TITLE_STYLE}>CHEER JAMBOREE</h2>
          <p style={SUBTITLE_STYLE}>Youth Cheerleading Showcase</p>
          <p style={TAGLINE_STYLE}>“{cheerJamboree.tagline}”</p>

          <div style={META_ROW_STYLE}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
              <Calendar size={14} style={{ color: '#FFD700' }} />
              <strong>{cheerJamboree.date}</strong>
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
              <MapPin size={14} style={{ color: '#FFD700' }} />
              {cheerJamboree.venue}
            </span>
          </div>
        </div>

        {/* 5 PROGRAM STEPS */}
        <div style={PROGRAM_GRID_STYLE} data-testid="cheer-flyer-program">
          {cheerJamboree.program.map((item) => (
            <div key={item.step} style={PROGRAM_CARD_STYLE}>
              <span style={STEP_NUM_STYLE}>STEP {item.step}</span>
              <div style={{ fontSize: 'clamp(1.1rem, 3vw, 1.6rem)', marginBottom: '0.25rem' }} aria-hidden>
                {item.emoji}
              </div>
              <p style={PROGRAM_TITLE_STYLE}>{item.title}</p>
            </div>
          ))}
        </div>

        {/* CONTACT + QR */}
        <div style={CONTACT_BOX_STYLE}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <div style={CONTACT_HEADER_STYLE}>Squads — Register With Our Coaches</div>
              <div style={CONTACT_LIST_STYLE}>
                {cheerJamboree.contacts.map((c) => (
                  <span
                    key={c.phone}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: '#fff' }}
                  >
                    <Phone size={13} style={{ color: '#FFD700' }} />
                    <strong>{c.name}</strong>&nbsp;·&nbsp;{c.phone}
                  </span>
                ))}
              </div>
              {cheerJamboree.email && (
                <div style={EMAIL_STYLE}>
                  <Mail size={13} />
                  {cheerJamboree.email}
                </div>
              )}
            </div>
            <JamboreeQR size={88} caption="SCAN · REGISTER" />
          </div>
        </div>
      </div>

      {/* Download button (outside the captured area) */}
      <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
        <Button
          className="btn-primary"
          onClick={handleDownload}
          disabled={downloading}
          data-testid="download-cheer-flyer-btn"
          style={{ fontWeight: 700 }}
        >
          {downloading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              &nbsp;Preparing PNG...
            </>
          ) : (
            <>
              <Download size={18} />
              &nbsp;Download Flyer as PNG
            </>
          )}
        </Button>

        <SocialShareButtons
          shareTitle="Randallstown Cheer Jamboree — Aug 8, 2026"
          testIdPrefix="cheer-share"
        />
      </div>
    </div>
  );
};

export default CheerJamboreeFlyer;
